import React, { useState } from "react";
import { Group, Slider, Title } from "@mantine/core";

interface SemiCircleAdjustmentProps {
  data: any;
  setData: React.Dispatch<React.SetStateAction<any>>;
  selectedDots: Record<string, any>;
  t: (key: string) => string;
  px?: string;
}

const SemiCircleAdjustment: React.FC<SemiCircleAdjustmentProps> = ({
  data,
  setData,
  selectedDots,
  t,
  px,
}) => {
  const [curveValue, setCurveValue] = useState<number>(0);

  const handleCurveChange = (value: number) => {
    const newData = JSON.parse(JSON.stringify(data));

    // Step 1: Get keys and dynamically calculate grid dimensions
    const keys = selectedDots;
    const rowsSet = new Set();
    const colsSet = new Set();
    const nameParts = new Set();

    keys.forEach((key: string) => {
      const parts = key.split(":");
      nameParts.add(parts[0]);
      rowsSet.add(parts[1]);
      colsSet.add(parts[2]);
    });

    const rows = Array.from(rowsSet);
    const cols = Array.from(colsSet);
    const namePart = Array.from(nameParts)[0];

    // Step 2: Adjust each row based on the number of dots
    rows.forEach((row) => {
      const rowKeys = cols.map((col) => `${namePart}:${row}:${col}`);
      const rowRects = rowKeys.map((key) => newData.data[key]?.rect);

      if (!rowRects || rowRects.length === 0) return;

      const rowLength = rowRects.length;
      const semiCircleRadius = Math.abs(value) * 2;
      const scalingFactor = 1.5;

      // Step 3: Apply movement and depth logic
      rowRects.forEach((rect, idx) => {
        if (rect) {
          const angle = (Math.PI / (rowLength - 1)) * idx;
          const yOffset = Math.sin(angle) * semiCircleRadius * scalingFactor;
          const xOffset =
            (idx - Math.floor(rowLength / 2)) * (Math.abs(value) / 10);

          // Apply vertical and horizontal movement
          rect.y = (
            parseFloat(rect.y || "0") - (value > 0 ? yOffset : -yOffset)
          ).toString();
          rect.x = (parseFloat(rect.x || "0") + xOffset).toString();
        }
      });
    });

    // Step 4: Update the modified data
    setData(newData);
  };

  return (
    <Group px={px}>
      <Title order={6}>
        {t("scheme.redactor.panelOptions.setSector.setSectorSemiCircle")}
      </Title>

      <Slider
        my={10}
        px={25}
        value={curveValue}
        onChange={(value) => {
          setCurveValue(value);
          handleCurveChange(value);
        }}
        w={"100%"}
        min={-30}
        max={30}
        step={5}
        label={curveValue}
      />
    </Group>
  );
};

export default React.memo(SemiCircleAdjustment);
